import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import QuickActions from "@/components/shared/QuickActions";
import OfflineBanner from "@/components/shared/OfflineBanner";
import {
  LayoutDashboard, Users, Package, ArrowDownToLine, Truck, Car,
  Shield, ArrowLeftRight, FileText, Settings, LogOut, Menu, X, Clock
} from "lucide-react";

const NAV_ITEMS = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/mod1", label: "People Movement", icon: Users, badge: "MOD-1" },
  { path: "/mod2", label: "Material Movement", icon: Package, badge: "MOD-2" },
  { path: "/mod3", label: "Incoming Material", icon: ArrowDownToLine, badge: "MOD-3" },
  { path: "/mod4", label: "Dispatch / Outgoing", icon: Truck, badge: "MOD-4" },
  { path: "/mod5", label: "Vehicle Movement", icon: Car, badge: "MOD-5" },
  { path: "/mod6", label: "Security & Logs", icon: Shield, badge: "MOD-6" },
  { path: "/mod7", label: "Inter-Plant Transfer", icon: ArrowLeftRight, badge: "MOD-7" },
  { path: "/vehicles", label: "Vehicles Master", icon: Car },
  { divider: true },
  { path: "/reports", label: "Reports", icon: FileText },
  { path: "/settings", label: "Settings", icon: Settings, allowedRoles: ["admin", "manager"] },
];

export default function AppLayout({ children }) {
  const { user, location, logout, updateLocation } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [clock, setClock] = useState(new Date());
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [routerLocation.pathname]);

  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden" data-testid="app-layout">
      {/* Company Bar */}
      <div className="bg-[#1B3C87] text-white px-3 sm:px-4 py-2 flex items-center justify-between shrink-0" data-testid="company-bar">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-1 shrink-0" data-testid="menu-toggle">
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <img src="/logo.png" alt="STURDFIX" className="h-7 sm:h-8 shrink-0" />
          <span className="hidden sm:inline text-white/40">|</span>
          <span className="font-black text-xs sm:text-sm tracking-wide uppercase truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <span className="hidden sm:inline">SINGHANIA INTERNATIONAL LTD.</span>
            <span className="sm:hidden">SIL</span>
          </span>
        </div>
        <span className="text-[10px] sm:text-xs text-white/60 hidden md:inline shrink-0">ISO 9001:2015 · IATF 16949 · CTPAT</span>
      </div>

      <OfflineBanner />

      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`
            fixed lg:relative inset-y-0 left-0 z-40
            w-60 sm:w-56 bg-[#122B63] text-white flex flex-col shrink-0
            transition-transform duration-200 ease-in-out
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
            lg:translate-x-0
          `}
          data-testid="sidebar"
        >
          {/* Mobile header in sidebar */}
          <div className="lg:hidden px-3 py-3 flex items-center justify-between border-b border-white/10">
            <img src="/logo.png" alt="STURDFIX" className="h-6" />
            <button onClick={() => setSidebarOpen(false)} className="p-1 text-white/60 hover:text-white">
              <X size={18} />
            </button>
          </div>
          <div className="px-3 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">
            Navigation
          </div>
          <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto">
            {NAV_ITEMS.map((item) => {
              if (item.divider) return <hr key="nav-divider" className="border-white/10 my-2" />;
              if (item.adminOnly && user?.role !== "admin") return null;
              if (item.allowedRoles && !item.allowedRoles.includes(user?.role)) return null;
              const Icon = item.icon;
              const active = routerLocation.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                  data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded text-sm transition-all ${
                    active
                      ? "bg-white/15 font-semibold border-l-2 border-white"
                      : "hover:bg-white/8 text-white/80"
                  }`}
                >
                  <Icon size={18} className="shrink-0" />
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })}
          </nav>
          <div className="p-3 border-t border-white/10 text-xs text-white/40">
            GMS v1.0 · Phase 1
          </div>
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Header */}
          <header className="bg-white border-b px-3 sm:px-4 py-2 sm:py-2.5 flex flex-col shrink-0" data-testid="app-header">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                <div className="hidden sm:flex items-center gap-1.5 text-sm font-mono text-gray-600 shrink-0">
                  <Clock size={14} />
                  {clock.toLocaleTimeString("en-IN", { hour12: true })}
                </div>
                <div className="hidden sm:block h-5 w-px bg-gray-200" />
                <Select value={location} onValueChange={updateLocation}>
                  <SelectTrigger className="w-[130px] sm:w-[160px] h-8 sm:h-9 text-xs sm:text-sm" data-testid="location-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Focal Point">Focal Point</SelectItem>
                    <SelectItem value="Sahnewal">Sahnewal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                <Badge className="bg-[#1B3C87] text-white text-[10px] tracking-wider uppercase hidden sm:inline-flex" data-testid="user-role-badge">
                  {user?.role}
                </Badge>
                <span className="text-xs sm:text-sm text-gray-700 hidden md:inline truncate max-w-[120px]">{user?.name}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { logout(); navigate("/login"); }}
                  data-testid="logout-btn"
                  className="h-8 sm:h-9 text-xs sm:text-sm"
                >
                  <LogOut size={14} className="sm:mr-1" />
                  <span className="hidden sm:inline">Sign Out</span>
                </Button>
              </div>
            </div>
            {/* Quick Actions Bar - horizontal scroll on mobile */}
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
              <QuickActions />
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 overflow-y-auto p-3 sm:p-5 bg-[#F4F5F7]" data-testid="main-content">
            <div className="animate-fadeIn">
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
